/**
 * InputManager — touch + keyboard controls for the horizontal phone-first build.
 *
 * Layout:
 *   - Left half of the screen = touch-drag movement joystick
 *     (drag vector = player velocity direction; max radius = MAX_DRAG_PX).
 *   - Three on-screen action buttons on the right side, drawn by GameScene:
 *     FIRE, CHOMP, CHARGE (hold-to-charge godzilla beam).
 *
 * Keyboard fallback (desktop dev testing):
 *   - WASD / arrow keys = movement
 *   - J = fire, K = chomp, L = hold for charge
 *
 * The manager exposes discrete signals (firePressed, chompPressed,
 * chargePressed/Released) and a continuous move vector.
 */
import Phaser from "phaser";

export interface InputState {
  move: Phaser.Math.Vector2; // normalised [-1, 1]
  fire: boolean;             // rising-edge this frame
  fireHeld: boolean;         // current frame
  chomp: boolean;            // rising-edge this frame
  /** Continuous chomp — true whenever the user is holding CHOMP. The
   *  GameScene's chomp cooldown gates the actual chomp fire so that
   *  holding the button still produces a series of chomps. */
  chompHeld: boolean;
  /** Continuous charge — true whenever BEAM is being held. */
  chargeHeld: boolean;
  chargePressed: boolean;    // rising-edge
  chargeReleased: boolean;   // falling-edge
}

export class InputManager {
  private scene: Phaser.Scene;
  private touchActive = false;
  private activePointerId: number | null = null;
  private touchStart: Phaser.Math.Vector2 = new Phaser.Math.Vector2();
  private touchCurrent: Phaser.Math.Vector2 = new Phaser.Math.Vector2();
  private readonly MAX_DRAG_PX = 90;

  // Action button pointer state
  private fireDown = false;
  private chompDown = false;
  private chargeDown = false;
  private lastFireDown = false;
  private lastChompDown = false;
  private lastChargeDown = false;

  // Keyboard state — desktop dev testing / PC players. We bind multiple
  // keys per action so muscle memory from classic shoot-em-ups works
  // out of the box:
  //   FIRE   = J, Space (also auto-fire when held)
  //   CHOMP  = X
  //   BEAM   = C  (hold to charge, release to fire)
  //   MOVE   = WASD or arrow keys
  private readonly FIRE_KEYS: string[] = ["J"];
  private readonly CHOMP_KEYS: string[] = ["X"];
  private readonly CHARGE_KEYS: string[] = ["C"];
  private keyFire: Phaser.Input.Keyboard.Key[] = [];
  private keyChomp: Phaser.Input.Keyboard.Key[] = [];
  private keyCharge: Phaser.Input.Keyboard.Key[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    scene.input.on("pointerdown", this.onPointerDown);
    scene.input.on("pointermove", this.onPointerMove);
    scene.input.on("pointerup", this.onPointerUp);
    scene.input.on("pointerupoutside", this.onPointerUp);
    if (scene.input.keyboard) {
      this.keyFire = this.FIRE_KEYS.map((k) => scene.input.keyboard!.addKey(k));
      this.keyChomp = this.CHOMP_KEYS.map((k) => scene.input.keyboard!.addKey(k));
      this.keyCharge = this.CHARGE_KEYS.map((k) => scene.input.keyboard!.addKey(k));
      // Prevent Space from scrolling the page when used for fire.
      scene.input.keyboard.addCapture("SPACE");
    }
  }

  // Returns true when any of the supplied keys are currently held down.
  private anyDown(keys: Phaser.Input.Keyboard.Key[]): boolean {
    for (const k of keys) if (k.isDown) return true;
    return false;
  }

  public setActionZones(fireZone: Phaser.GameObjects.Zone | null, chompZone: Phaser.GameObjects.Zone | null, chargeZone: Phaser.GameObjects.Zone | null): void {
    // Hooks into Phaser's hit-test — we listen on the zone's pointerdown/move/up
    // events directly. Called once by GameScene during scene setup.
    if (fireZone) {
      fireZone.setInteractive({ useHandCursor: false });
      fireZone.on("pointerdown", () => (this.fireDown = true));
      fireZone.on("pointerup", () => (this.fireDown = false));
      fireZone.on("pointerupoutside", () => (this.fireDown = false));
    }
    if (chompZone) {
      chompZone.setInteractive({ useHandCursor: false });
      chompZone.on("pointerdown", () => (this.chompDown = true));
      chompZone.on("pointerup", () => (this.chompDown = false));
      chompZone.on("pointerupoutside", () => (this.chompDown = false));
    }
    if (chargeZone) {
      chargeZone.setInteractive({ useHandCursor: false });
      chargeZone.on("pointerdown", () => (this.chargeDown = true));
      chargeZone.on("pointerup", () => (this.chargeDown = false));
      chargeZone.on("pointerupoutside", () => (this.chargeDown = false));
    }
  }

  private onPointerDown = (p: Phaser.Input.Pointer): void => {
    // Movement joystick only engages for pointers in the LEFT half of the
    // screen, leaving the right half for the action buttons.
    if (p.x > this.scene.scale.width * 0.55) return;
    if (this.activePointerId !== null) return; // already tracking a movement pointer
    
    this.activePointerId = p.id;
    this.touchActive = true;
    this.touchStart.set(p.x, p.y);
    this.touchCurrent.copy(this.touchStart);
  };

  private onPointerMove = (p: Phaser.Input.Pointer): void => {
    if (this.activePointerId !== p.id) return;
    this.touchCurrent.set(p.x, p.y);
  };

  private onPointerUp = (p: Phaser.Input.Pointer): void => {
    if (this.activePointerId === p.id) {
      this.touchActive = false;
      this.activePointerId = null;
    }
  };

  public read(): InputState {
    const out: InputState = {
      move: new Phaser.Math.Vector2(),
      fire: false,
      fireHeld: false,
      chomp: false,
      chompHeld: false,
      chargeHeld: false,
      chargePressed: false,
      chargeReleased: false,
    };

    // Movement: keyboard takes precedence over touch.
    let kx = 0;
    let ky = 0;
    const keys = this.scene.input.keyboard;
    if (keys) {
      const left = keys.addKey("A")!.isDown || keys.addKey("LEFT")!.isDown;
      const right = keys.addKey("D")!.isDown || keys.addKey("RIGHT")!.isDown;
      const up = keys.addKey("W")!.isDown || keys.addKey("UP")!.isDown;
      const down = keys.addKey("S")!.isDown || keys.addKey("DOWN")!.isDown;
      if (left) kx -= 1;
      if (right) kx += 1;
      if (up) ky -= 1;
      if (down) ky += 1;
    }

    if (kx !== 0 || ky !== 0) {
      out.move.set(kx, ky).normalize();
    } else if (this.touchActive) {
      const dx = this.touchCurrent.x - this.touchStart.x;
      const dy = this.touchCurrent.y - this.touchStart.y;
      const len = Math.hypot(dx, dy);
      if (len > 4) {
        const clamped = Math.min(len, this.MAX_DRAG_PX);
        out.move.set((dx / len) * (clamped / this.MAX_DRAG_PX), (dy / len) * (clamped / this.MAX_DRAG_PX));
      }
    }

    // Fire: keyboard OR touch button.
    const kFire = this.anyDown(this.keyFire);
    const kChomp = this.anyDown(this.keyChomp);
    const kCharge = this.anyDown(this.keyCharge);
    out.fireHeld = this.fireDown || kFire;
    out.fire = out.fireHeld && !this.lastFireDown;
    this.lastFireDown = out.fireHeld;

    // Chomp: continuous. The GameScene's chomp cooldown handles rate-limiting;
    // we don't gate behind a rising-edge here. Anything that says "the user
    // wants to chomp right now" sets this true.
    out.chompHeld = this.chompDown || kChomp;
    // Rising edge still exposed for one-shot triggers (UI presses etc.).
    out.chomp = out.chompHeld && !this.lastChompDown;
    this.lastChompDown = out.chompHeld;

    const chargeNow = this.chargeDown || kCharge;
    out.chargeHeld = chargeNow;
    out.chargePressed = chargeNow && !this.lastChargeDown;
    out.chargeReleased = !chargeNow && this.lastChargeDown;
    this.lastChargeDown = chargeNow;

    return out;
  }
}